import { yupResolver } from "@hookform/resolvers/yup";
import { DEFAULT_SECTION_MARGINS, MainPageHeroContent, SectionMargins } from "api/shop";
import {
  HeadingTagSelect,
  PaziaFileDropzone,
  PaziaFormButton,
  PaziaInput,
  PaziaSelect,
} from "components/atoms";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { MdAdd, MdDelete } from "react-icons/md";
import * as yup from "yup";
import { ButtonVariantSelect } from "./ButtonVariantSelect";
import { MARGINS_FORM_NAME, marginsSchema, SectionMarginsConfig } from "./SectionMarginsConfig";
import { useShopImageUpload } from "./useShopImageUpload";

interface MainPageHeroFormProps {
  onSubmit: (data: MainPageHeroFormValues) => void;
  onCancel: () => void;
  defaultValues?: MainPageHeroContent;
  isLoading?: boolean;
}

export const MainPageHeroForm = ({ onSubmit, onCancel, defaultValues, isLoading }: MainPageHeroFormProps) => {
  const { uploadImage, uploadImages } = useShopImageUpload();
  const methods = useForm<FormValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      [FORM_NAMES.HEADER_LINE_1]: defaultValues?.headerLine1 ?? "",
      [FORM_NAMES.HEADER_LINE_2]: defaultValues?.headerLine2 ?? "",
      [FORM_NAMES.HEADER_LINE_1_TAG]: defaultValues?.headerLine1Tag ?? "h1",
      [FORM_NAMES.HEADER_LINE_2_TAG]: defaultValues?.headerLine2Tag ?? "h2",
      [FORM_NAMES.BG_IMAGES]: defaultValues?.bgImages ?? [{ src: "", alt: "" }],
      [FORM_NAMES.BUTTONS]: defaultValues?.buttons ?? [],
      [FORM_NAMES.TILES]: defaultValues?.tiles ?? [],
      [MARGINS_FORM_NAME]: (defaultValues as any)?.margins ?? DEFAULT_SECTION_MARGINS,
    },
  });
  const { handleSubmit, control, formState } = methods;
  const {
    fields: bgImageFields,
    append: appendBgImage,
    remove: removeBgImage,
  } = useFieldArray({
    control,
    name: FORM_NAMES.BG_IMAGES,
  });

  const {
    fields: buttonFields,
    append: appendButton,
    remove: removeButton,
  } = useFieldArray({
    control,
    name: FORM_NAMES.BUTTONS,
  });

  const {
    fields: tileFields,
    append: appendTile,
    remove: removeTile,
  } = useFieldArray({
    control,
    name: FORM_NAMES.TILES,
  });

  const handleFormSubmit = async (data: FormValues) => {
    const uploadedBgImages = await uploadImages(data.bgImages);
    const uploadedTiles = await Promise.all(
      data.tiles.map(async (tile) => ({
        ...tile,
        icon: await uploadImage(tile.icon),
      })),
    );

    const formattedData: MainPageHeroFormValues = {
      headerLine1: data.headerLine1,
      headerLine2: data.headerLine2,
      headerLine1Tag: data.headerLine1Tag as any,
      headerLine2Tag: data.headerLine2Tag as any,
      bgImages: uploadedBgImages.map((img) => ({
        src: img.src,
        alt: img.alt,
      })),
      buttons: data.buttons.map((btn) => ({
        label: btn.label,
        variant: btn.variant,
        href: btn.href,
      })),
      tiles: uploadedTiles.map((tile) => ({
        icon: tile.icon,
        label: tile.label,
        subtitle: tile.subtitle,
      })),
      margins: data.margins,
    };
    onSubmit(formattedData);
  };

  const addBgImage = () => {
    appendBgImage({ src: "", alt: "" });
  };

  const addButton = () => {
    appendButton({ label: "", variant: "primary", href: "" });
  };

  const addTile = () => {
    appendTile({ icon: "", label: "", subtitle: "" });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handleFormSubmit)} className="grid gap-4">
        {/* Header Lines */}
        <div className="grid gap-3 md:grid-cols-2">
          <PaziaInput name={FORM_NAMES.HEADER_LINE_1} label="Nagłówek linia 1" required />
          <PaziaInput name={FORM_NAMES.HEADER_LINE_2} label="Nagłówek linia 2" required />
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <HeadingTagSelect name={FORM_NAMES.HEADER_LINE_1_TAG} label="Tag nagłówka linia 1" />
          <HeadingTagSelect name={FORM_NAMES.HEADER_LINE_2_TAG} label="Tag nagłówka linia 2" />
        </div>

        {/* Background Images Section */}
        <div className="border-t border-slate-200 pt-4 dark:border-slate-600">
          <div className="mb-3 flex items-center justify-between">
            <h4 className="font-medium text-slate-700 dark:text-slate-300">Obrazy tła</h4>
            <button
              type="button"
              onClick={addBgImage}
              className="flex items-center gap-1 rounded-lg bg-indigo-100 px-3 py-1.5 text-sm font-medium text-indigo-600 transition-colors hover:bg-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-400 dark:hover:bg-indigo-900/50"
            >
              <MdAdd size={18} />
              Dodaj obraz
            </button>
          </div>

          {bgImageFields.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Brak obrazów tła. Kliknij "Dodaj obraz", aby dodać.
            </p>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {bgImageFields.map((field, index) => (
                <div
                  key={field.id}
                  className="rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-600 dark:bg-slate-800"
                >
                  <div className="mb-3 flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Obraz #{index + 1}</span>
                    <button
                      type="button"
                      onClick={() => removeBgImage(index)}
                      className="rounded p-1.5 text-slate-500 transition-colors hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400"
                      title="Usuń obraz"
                    >
                      <MdDelete size={18} />
                    </button>
                  </div>
                  <div className="space-y-3">
                    <div className="[&_img]:max-h-32">
                      <PaziaFileDropzone
                        name={`${FORM_NAMES.BG_IMAGES}.${index}.src`}
                        label="Obraz"
                        type="image"
                        single
                        preview="image"
                      />
                    </div>
                    <PaziaInput
                      name={`${FORM_NAMES.BG_IMAGES}.${index}.alt`}
                      label="Tekst alternatywny (alt)"
                      required
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Buttons Section */}
        <div className="border-t border-slate-200 pt-4 dark:border-slate-600">
          <div className="mb-3 flex items-center justify-between">
            <h4 className="font-medium text-slate-700 dark:text-slate-300">Przyciski</h4>
            <button
              type="button"
              onClick={addButton}
              className="flex items-center gap-1 rounded-lg bg-indigo-100 px-3 py-1.5 text-sm font-medium text-indigo-600 transition-colors hover:bg-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-400 dark:hover:bg-indigo-900/50"
            >
              <MdAdd size={18} />
              Dodaj przycisk
            </button>
          </div>

          {buttonFields.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Brak przycisków. Kliknij "Dodaj przycisk", aby dodać.
            </p>
          ) : (
            <div className="space-y-3">
              {buttonFields.map((field, index) => (
                <div
                  key={field.id}
                  className="flex items-end gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-600 dark:bg-slate-800"
                >
                  <div className="flex-1">
                    <PaziaInput name={`${FORM_NAMES.BUTTONS}.${index}.label`} label="Etykieta" required />
                  </div>
                  <div className="w-40">
                    <ButtonVariantSelect
                      name={`${FORM_NAMES.BUTTONS}.${index}.variant`}
                      required
                    />
                  </div>
                  <div className="flex-1">
                    <PaziaInput name={`${FORM_NAMES.BUTTONS}.${index}.href`} label="Link (href)" required />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeButton(index)}
                    className="mb-1 rounded p-1.5 text-slate-500 transition-colors hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400"
                    title="Usuń przycisk"
                  >
                    <MdDelete size={18} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Tiles Section */}
        <div className="border-t border-slate-200 pt-4 dark:border-slate-600">
          <div className="mb-3 flex items-center justify-between">
            <h4 className="font-medium text-slate-700 dark:text-slate-300">Kafelki</h4>
            <button
              type="button"
              onClick={addTile}
              className="flex items-center gap-1 rounded-lg bg-indigo-100 px-3 py-1.5 text-sm font-medium text-indigo-600 transition-colors hover:bg-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-400 dark:hover:bg-indigo-900/50"
            >
              <MdAdd size={18} />
              Dodaj kafelek
            </button>
          </div>

          {tileFields.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Brak kafelków. Kliknij "Dodaj kafelek", aby dodać.
            </p>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {tileFields.map((field, index) => (
                <div
                  key={field.id}
                  className="rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-600 dark:bg-slate-800"
                >
                  <div className="mb-3 flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Kafelek #{index + 1}</span>
                    <button
                      type="button"
                      onClick={() => removeTile(index)}
                      className="rounded p-1.5 text-slate-500 transition-colors hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400"
                      title="Usuń kafelek"
                    >
                      <MdDelete size={18} />
                    </button>
                  </div>
                  <div className="space-y-3">
                    <div className="[&_img]:max-h-16">
                      <PaziaFileDropzone
                        name={`${FORM_NAMES.TILES}.${index}.icon`}
                        label="Ikona"
                        type="image"
                        single
                        preview="image"
                      />
                    </div>
                    <PaziaInput name={`${FORM_NAMES.TILES}.${index}.label`} label="Etykieta" required />
                    <PaziaInput name={`${FORM_NAMES.TILES}.${index}.subtitle`} label="Podtytuł" required />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <SectionMarginsConfig />

        <div className="flex justify-end gap-2 border-t border-slate-200 pt-4 dark:border-slate-600">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-slate-300 px-4 py-2 text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            Anuluj
          </button>
          <PaziaFormButton disabled={isLoading} />
        </div>
      </form>
    </FormProvider>
  );
};

const FORM_NAMES = {
  HEADER_LINE_1: "headerLine1",
  HEADER_LINE_2: "headerLine2",
  HEADER_LINE_1_TAG: "headerLine1Tag",
  HEADER_LINE_2_TAG: "headerLine2Tag",
  BG_IMAGES: "bgImages",
  BUTTONS: "buttons",
  TILES: "tiles",
} as const;

const bgImageSchema = yup.object({
  src: yup.mixed().required("Obraz jest wymagany"),
  alt: yup.string().required("Tekst alternatywny jest wymagany"),
});

const buttonSchema = yup.object({
  label: yup.string().required("Etykieta jest wymagana"),
  variant: yup.string().required("Wariant jest wymagany"),
  href: yup.string().required("Link jest wymagany"),
});

const tileSchema = yup.object({
  icon: yup.mixed().required("Ikona jest wymagana"),
  label: yup.string().required("Etykieta jest wymagana"),
  subtitle: yup.string().required("Podtytuł jest wymagany"),
});

const schema = yup
  .object({
    [FORM_NAMES.HEADER_LINE_1]: yup.string().required("Nagłówek linia 1 jest wymagany"),
    [FORM_NAMES.HEADER_LINE_2]: yup.string().required("Nagłówek linia 2 jest wymagany"),
    [FORM_NAMES.HEADER_LINE_1_TAG]: yup.string(),
    [FORM_NAMES.HEADER_LINE_2_TAG]: yup.string(),
    [FORM_NAMES.BG_IMAGES]: yup.array().of(bgImageSchema).min(1, "Dodaj przynajmniej jeden obraz tła").required(),
    [FORM_NAMES.BUTTONS]: yup.array().of(buttonSchema),
    [FORM_NAMES.TILES]: yup.array().of(tileSchema),
    [MARGINS_FORM_NAME]: marginsSchema,
  })
  .required();

interface FormValues {
  headerLine1: string;
  headerLine2: string;
  headerLine1Tag: string;
  headerLine2Tag: string;
  bgImages: Array<{ src: string | File; alt: string }>;
  buttons: Array<{ label: string; variant: "primary" | "secondary" | "outline"; href: string }>;
  tiles: Array<{ icon: string | File; label: string; subtitle: string }>;
  margins: SectionMargins;
}

export interface MainPageHeroFormValues {
  headerLine1: string;
  headerLine2: string;
  headerLine1Tag?: string;
  headerLine2Tag?: string;
  bgImages: Array<{ src: string; alt: string }>;
  buttons: Array<{ label: string; variant: "primary" | "secondary" | "outline"; href: string }>;
  tiles: Array<{ icon: string; label: string; subtitle: string }>;
  margins?: SectionMargins;
}
